/**
 * `name` — the item name column (`specs/M2-tier1-column-types.md` §5.1, `[09§A.7 name]`).
 *
 * Auto-created on every board, never returned inside `column_values` (there is no `NameValue`
 * GraphQL type `[cvs§2 name]`), but `name` *is* a member of the `ColumnType` enum, so it is a
 * registry entry like every other type and filters/sort/export/forms/automations treat it
 * uniformly. It lives in `items.name`: **no `cell_values` row ever** (M2 §4).
 *
 * Write shape: `{"name": "Actual Task Name"}` — a plain string. Create N / Update Y / Clear N.
 * The activity event is `update_name`, not `update_column_value`, and carries
 * `previous_value: {name}` / `value: {name}` `[ba§2]`.
 */
import { z } from 'zod';
import { DEFAULT_SQL_REFS, defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
import { nameFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
/** monday's real cap is unrecorded; 255 is ASSUMED (open question Q65). */
export const NAME_MAX_LENGTH = 255;
export const nameValueSchema = z.string().max(NAME_MAX_LENGTH);
export const nameSettingsSchema = z.looseObject({});
function coerce(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string')
        return raw;
    if (typeof raw === 'object') {
        const record = raw;
        const candidate = record.name ?? record.text;
        if (typeof candidate === 'string')
            return candidate;
    }
    throw new ColumnValueError('name', `expected a string, received ${typeof raw}`);
}
function validate(value) {
    if (value === null)
        return null;
    const parsed = nameValueSchema.safeParse(value);
    if (!parsed.success) {
        throw new ColumnValueError('name', parsed.error.issues[0]?.message ?? 'invalid');
    }
    return parsed.data;
}
export const nameColumn = defineColumnType({
    type: 'name',
    title: 'Name',
    description: 'The item name. Every board has exactly one.',
    apiFragmentName: null,
    legacyTypeName: 'name',
    idPrefix: 'name',
    valueSchema: nameValueSchema,
    settingsSchema: nameSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: nameSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    /** *"Clear N"* `[09§A.7 name]` — an item always has a name. */
    clearForms: [],
    filterConditions: nameFilterConditions,
    filterOperators: operatorsOf(nameFilterConditions),
    aggregations: ['count', 'count_filled', 'count_empty'],
    emptyText: '',
    parseValue(raw) {
        return validate(coerce(raw));
    },
    parseSimpleValue(raw) {
        return validate(coerce(raw));
    },
    toText(value) {
        return value ?? '';
    },
    toSortKeys(value) {
        return { text: textShadow(value), number: null, date: null };
    },
    isEqual(a, b) {
        return (a ?? null) === (b ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { name: value };
    },
    fromMondayValue(raw) {
        return coerce(raw);
    },
    toMondayValue(value) {
        return value;
    },
    toSimpleValue(value) {
        return value ?? '';
    },
    fromMondaySettings(raw) {
        return nameSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings() {
        return {};
    },
    toMondaySettingsStr() {
        return {};
    },
    toApiFragment() {
        // `name` has no `ColumnValue` fragment at all; readers use `Item.name`.
        return {};
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value) {
        return value ? [{ key: value, label: value }] : [];
    },
    /**
     * **Projected, so the SQL reads `items.name` (P-051).** `name` has no `cell_values` row on any
     * board — the value lives in `items.name` (M2 §5.1) — so every shadow is null for every item
     * and the default text operators compared three NULLs: `is` / `contains` / `starts with`
     * matched nothing at all. The override points them at the column the value actually lives in,
     * so the grid, the filter, the sort and `/v2` read one definition.
     *
     * No emptiness branch: monday offers no empty test on `name` (`filters.ts`, an item always
     * has a name), so `is_empty` never reaches here.
     */
    filterSql(ctx) {
        const refs = ctx.refs ?? DEFAULT_SQL_REFS;
        return defaultFilterSql('text', ctx, `${refs.item}.name`);
    },
    /**
     * **Case-insensitive, like monday's own Sort by Name** (P-051, the reported no-op): `lower()`
     * first so `apple` sorts before `Banana` rather than after it (`B` < `a` in C collation), the
     * raw name second so two names differing only in case still have a deterministic order.
     */
    sortSql(refs) {
        return [`lower(${refs.item}.name)`, `${refs.item}.name`];
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map